import { z } from 'zod';

/**
 * Central search client (replit.md §0.8). Calls the single `/api/search`
 * surface, validates the payload at the boundary (every external boundary is
 * Zod-checked — anti-slop rule), and returns the typed, grouped results the
 * Cmd+K palette renders. No component talks to search any other way.
 */
const actionSchema = z.object({
  type: z.string(),
  label: z.string(),
  href: z.string().nullable(),
});

const hitSchema = z.object({
  entityType: z.string(),
  entityId: z.string(),
  title: z.string(),
  subtitle: z.string().nullable(),
  href: z.string().nullable(),
  icon: z.string().nullable(),
  actions: z.array(actionSchema),
  rank: z.number(),
});

const groupSchema = z.object({
  type: z.string(),
  results: z.array(hitSchema),
});

const responseSchema = z.object({
  query: z.string(),
  groups: z.array(groupSchema),
});

export type SearchAction = z.infer<typeof actionSchema>;
export type SearchHit = z.infer<typeof hitSchema>;
export type SearchGroup = z.infer<typeof groupSchema>;
export type SearchResponse = z.infer<typeof responseSchema>;

export interface FetchSearchOptions {
  /** Injectable fetch (tests). Defaults to the global fetch. */
  fetchImpl?: typeof fetch;
  /** Base URL; defaults to same-origin (relative) so it works in every environment. */
  baseUrl?: string;
  /** Supabase access token; attached as a Bearer header when present. */
  accessToken?: string;
  /** Max results (server caps at 50). */
  limit?: number;
}

export async function fetchSearch(query: string, options: FetchSearchOptions = {}): Promise<SearchResponse> {
  const { fetchImpl = fetch, baseUrl = '', accessToken, limit } = options;
  const params = new URLSearchParams({ q: query });
  if (limit !== undefined) {
    params.set('limit', String(limit));
  }
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const res = await fetchImpl(`${baseUrl}/api/search?${params.toString()}`, { headers });
  if (!res.ok) {
    throw new Error(`Search request failed: ${res.status}`);
  }
  return responseSchema.parse(await res.json());
}
